import { apiGet } from './api-client';

/** Raw stop finder result from TfNSW */
interface TfNSWStopLocation {
  id: string;
  name: string;
  disassembledName?: string;
  parent?: { id: string; name: string };
  type?: string;
  coord?: [number, number];
  modes?: number[];
  assignedStops?: TfNSWStopLocation[];
}

interface TfNSWStopFinderResponse {
  locations: TfNSWStopLocation[];
}

/** Cleaned-up stop result for the UI */
export interface StopResult {
  id: string;
  name: string;
  parentName?: string;
  latitude?: number;
  longitude?: number;
  modes: number[];
}

const stopSearchCache = new Map<string, { at: number; results: StopResult[] }>();

// Lazy-loaded client-side stop index (station/wharf/interchange names) used as
// a fallback when the TfNSW stop_finder API returns poor results for short or
// partial queries (e.g. "strath" returns nothing for Strathfield).
let stopsIndexPromise: Promise<StopResult[]> | undefined;
function loadStopsIndex(): Promise<StopResult[]> {
  stopsIndexPromise ||= fetch('/data/stops-index.json', { cache: 'no-cache' })
    .then(r => (r.ok ? r.json() : { stops: [] }))
    .then((data: { stops?: [string, string, number, number][] }) =>
      (data.stops || []).map(([id, name, lon, lat]) => ({
        id, name, latitude: lat, longitude: lon, modes: [],
      })),
    )
    .catch(() => []);
  return stopsIndexPromise;
}

/** Score a stop name against a query (higher = better). */
export function scoreStop(name: string, q: string, qWords: string[]): number {
  const n = name.toLocaleLowerCase();
  if (n === q) return 1000;
  if (n.startsWith(q)) return 800;
  if (n.includes(q)) return 600;
  const matched = qWords.filter(w => n.includes(w)).length;
  return (matched / Math.max(qWords.length, 1)) * 400;
}

// Derive a stable "station key" from a stop name so platform/stand variants
// collapse to a single entry (e.g. "Strathfield Station, Platform 1" and
// "Strathfield Station, Albert Rd, Stand E" both -> "Strathfield Station").
export function stationKey(name: string): string {
  let n = name.trim();
  // Strip platform/stand/bay/wharf/side/terminal variants.
  n = n.replace(/,\s*(platform\s*\d+|stand\s*[a-z0-9]+|bay\s*\d+|wharf\s*\d*|side\s*[ab]|terminal\s*pl\s*-\s*stand\s*\d+)$/i, '');
  // Strip a trailing road/street detail (e.g. "Albert Rd, Stand E").
  n = n.replace(/,\s*(?:[a-z0-9' ]+?\s+(?:rd|road|st|street|av|ave|avenue|pde|parade|hwy|highway|twy|way|dr|drive|cres|cr|ct|court|pl|place|sq|square|bvd|blvd|ln|lane|tce|terrace|loop|cl|close|gr|grove|pky|pkwy|parkway|mews|row|walk|circuit|cct|esplanade|promenade|wharf|jetty|ferry|terminal|interchange|station|centre|center|square|junction|stand|platform|bay|side|gate|entrance|exit|north|south|east|west|opp|opposite|near|at|before|after|via|the|of|and|&|st|mt|pt|nth|sth)\s*[a-z0-9]*)$/i, '');
  // Strip a trailing locality name, but only when the base ends with a transit
  // keyword (e.g. "Strathfield Station, Strathfield" -> "Strathfield Station").
  // This avoids collapsing distinct bus stops that share a name across suburbs.
  n = n.replace(/,\s*[A-Z][A-Za-z' -]*$/, (m) =>
    /(?:station|wharf|interchange|terminal|square|centre|center|junction|stand|platform)\s*$/i.test(n.replace(/,\s*[A-Z][A-Za-z' -]*$/, '')) ? '' : m,
  );
  return n.trim() || name.trim();
}

export async function searchStops(query: string, signal?: AbortSignal): Promise<StopResult[]> {
  if (!query || query.length < 2) return [];
  const cacheKey = query.trim().toLocaleLowerCase();
  const cached = !signal ? stopSearchCache.get(cacheKey) : undefined;
  if (cached && Date.now() - cached.at < 5 * 60_000) return cached.results;

  const q = query.trim().toLocaleLowerCase();
  const qWords = q.split(/\s+/).filter(Boolean);

  // Always load the local GTFS stop index (deduplicated by station) and score
  // it against the query. This gives consistent, deterministic results
  // regardless of query length, and surfaces major stations (e.g. Strathfield
  // Station) that the TfNSW API buries or omits for short/partial queries.
  const index = await loadStopsIndex();
  const local = index
    .map((s) => ({ s, score: scoreStop(s.name, q, qWords) }))
    .filter(({ score }) => score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 8)
    .map(({ s }) => s);

  // Query the TfNSW API too, to catch stops not in the local index (e.g. bus
  // stops on minor roads). Merge and re-rank so the best matches surface.
  let apiResults: StopResult[] = [];
  try {
    const data = await apiGet<TfNSWStopFinderResponse>('/tp/stop_finder', {
      params: {
        type_sf: 'any',
        name_sf: query,
        outputFormat: 'rapidJSON',
        coordOutputFormat: 'EPSG:4326',
      },
      signal,
    });

    if (data?.locations) {
      apiResults = data.locations
        .filter((loc) => loc.type === 'stop')
        .map((loc) => {
          const name = (loc.name || '').toLocaleLowerCase();
          const disassembled = (loc.disassembledName || '').toLocaleLowerCase();
          const haystack = `${name} ${disassembled}`;
          let relevance = 0;
          if (name === q || disassembled === q) relevance = 1000;
          else if (name.startsWith(q) || disassembled.startsWith(q)) relevance = 800;
          else if (name.includes(q) || disassembled.includes(q)) relevance = 600;
          else {
            const matched = qWords.filter(w => haystack.includes(w)).length;
            relevance = matched / Math.max(qWords.length, 1) * 400;
          }
          const priority = (loc.modes || []).some((m) => [1, 2, 4, 8, 9].includes(m)) ? 200 : 0;
          return { loc, relevance, priority };
        })
        .sort((a, b) => (b.relevance + b.priority) - (a.relevance + a.priority))
        .slice(0, 8)
        .map(({ loc }) => {
          const child = loc.assignedStops?.[0];
          return {
            id: loc.id,
            name: loc.name,
            parentName: loc.parent?.name,
            latitude: loc.coord?.[0] ?? child?.coord?.[0],
            longitude: loc.coord?.[1] ?? child?.coord?.[1],
            modes: loc.modes || [],
          };
        });
    }
  } catch (e) {
    if (signal?.aborted) throw e;
    // Fall through to local index on API failure.
  }

  // Merge local + API results, dedupe by station key, and re-rank so the best
  // matches (e.g. Strathfield Station) surface above weak API hits. When a
  // station key collides, prefer the cleaner local index entry (e.g.
  // "Strathfield Station" over "Strathfield Station, Albert Rd, Stand E").
  const seen = new Map<string, StopResult>();
  const merged = [...local, ...apiResults]
    .map((r) => ({ r, key: stationKey(r.name).toLocaleLowerCase(), score: scoreStop(stationKey(r.name), q, qWords) }))
    .sort((a, b) => b.score - a.score)
    .filter(({ r, key }) => {
      const existing = seen.get(key);
      if (!existing) { seen.set(key, r); return true; }
      // Prefer the local index entry (cleaner station name) on collision.
      if (local.includes(r) && !local.includes(existing)) { seen.set(key, r); return true; }
      return false;
    })
    .slice(0, 8)
    .map(({ r }) => r);

  if (!signal) stopSearchCache.set(cacheKey, { at: Date.now(), results: merged });
  return merged;
}
